import { Injectable } from '@angular/core';

export interface IdbStorage {
  idbGet<T>(key: string): Promise<T | undefined>;
  idbSet(key: string, value: unknown): Promise<void>;
  idbDelete(key: string): Promise<void>;
  idbGetAllKeys(): Promise<string[]>;
}

const DB_VERSION = 1;
const STORE_NAME = 'data';

@Injectable({ providedIn: 'root' })
export class IdbStorageService {
  createStorage(dbName: string): IdbStorage {
    let dbPromise: Promise<IDBDatabase> | null = null;

    function openDb(): Promise<IDBDatabase> {
      if (dbPromise) return dbPromise;
      dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(dbName, DB_VERSION);
        request.onupgradeneeded = () => {
          request.result.createObjectStore(STORE_NAME);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      return dbPromise;
    }

    return {
      async idbGet<T>(key: string): Promise<T | undefined> {
        const db = await openDb();
        return new Promise<T | undefined>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readonly');
          const request = tx.objectStore(STORE_NAME).get(key);
          request.onsuccess = () => resolve(request.result as T | undefined);
          request.onerror = () => reject(request.error);
        });
      },

      async idbSet(key: string, value: unknown): Promise<void> {
        const db = await openDb();
        return new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.objectStore(STORE_NAME).put(value, key);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      },

      async idbDelete(key: string): Promise<void> {
        const db = await openDb();
        return new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.objectStore(STORE_NAME).delete(key);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      },

      async idbGetAllKeys(): Promise<string[]> {
        const db = await openDb();
        return new Promise<string[]>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readonly');
          const request = tx.objectStore(STORE_NAME).getAllKeys();
          request.onsuccess = () => resolve(request.result as string[]);
          request.onerror = () => reject(request.error);
        });
      },
    };
  }
}
